import { useTranslation } from '@mukil/i18n';
import { Alert, GlassCard } from '@jumpstone/ui-react';
import { NumericFieldRow } from '../../components/common/field_row';
import { net_payable_reconciles } from './pay_run_calculations';
import { format_date, format_money } from '../../lib/format';
import type { PayRun } from '../../data/types';
import { PayRunContractorBlockedBanner } from './pay_run_contractor_blocked_banner';

/** Sticky totals panel beside the wizard. */
export function PayRunSummaryCard({ run }: { run: PayRun }): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const reconciles = net_payable_reconciles(run);
  const blocked_contractor_count = run.blocked_contractor_count ?? 0;

  if (blocked_contractor_count > 0) {
    // The totals are the API's placeholder zeros for a blocked draft — never
    // print them as money (WagePro worker types).
    return (
      <GlassCard
        variant="accent"
        className="rounded-2xl p-5 lg:sticky lg:top-[calc(var(--wp-admin-header-h)+16px)]"
      >
        <p className="text-[11.5px] uppercase tracking-wider font-bold text-[color:var(--ink-muted)] mb-2.5">
          {t('pay_run.summary')}
        </p>
        <PayRunContractorBlockedBanner count={blocked_contractor_count} />
      </GlassCard>
    );
  }

  return (
    <GlassCard
      variant="accent"
      className="rounded-2xl p-5 lg:sticky lg:top-[calc(var(--wp-admin-header-h)+16px)]"
    >
      <p className="text-[11.5px] uppercase tracking-wider font-bold text-[color:var(--ink-muted)] mb-2.5">
        {t('pay_run.summary')}
      </p>
      <NumericFieldRow label={t('pay_run.gross_wages')}>
        {format_money(run.gross_wages, run.currency, locale)}
      </NumericFieldRow>
      <NumericFieldRow label={t('pay_run.total_allowances')}>
        {format_money(run.total_allowances, run.currency, locale)}
      </NumericFieldRow>
      <NumericFieldRow label={t('pay_run.statutory_deductions')}>
        {format_money(run.statutory_deductions, run.currency, locale)}
      </NumericFieldRow>
      {/*
        Custom standing deductions get their own row rather than being folded
        into "Statutory deductions" (#24395). Hidden when zero: a run with no
        welfare fund or loan repayments has nothing to disclose, and an
        always-present "LKR 0" row is noise on a card read at a glance.
      */}
      {run.other_deductions !== 0 && (
        <NumericFieldRow label={t('pay_run.other_deductions')}>
          {format_money(run.other_deductions, run.currency, locale)}
        </NumericFieldRow>
      )}
      {/*
        ALWAYS the authoritative `net_payable` — never a figure this card
        derives from its own rows. `deriveDeductionSplit` clamps a
        contradictory residual at 0 (`wagepro_store_pay_runs.ts`), which
        breaks the `gross - statutory - other === net` identity; printing the
        derived total then would show a number that is not what anyone gets
        paid, and can even go negative (a draft line whose custom deductions
        exceed its gross). When the split does not reconcile we keep the real
        net and disclose the unreconciled breakdown in the note below —
        the discrepancy is surfaced, never hidden and never papered over
        with an invented total.
      */}
      <div className="flex justify-between pt-3 font-bold text-[15px]">
        <span>{t('pay_run.net_payable')}</span>
        <span className="num">
          {format_money(run.net_payable, run.currency, locale)}
        </span>
      </div>
      {!reconciles && (
        <Alert variant="info" className="mt-3">
          {t('pay_run.deduction_split_unreconciled')}
        </Alert>
      )}
      <p className="mt-2.5 text-[12px] text-[color:var(--ink-muted)]">
        {t('pay_run.filing_due', {
          date: format_date(run.filing_due_on, locale),
        })}
      </p>
    </GlassCard>
  );
}
